/**
 * react-i18next initialization (CSPS §4).
 *
 * Two locales: ms (Bahasa Melayu, default) and en (English).
 * Persistence is via localStorage so the choice survives page reloads.
 *
 * Translation strings are inline for the pilot — there are <100 of them
 * and a separate JSON file just adds friction. Once the catalog grows
 * past ~200 keys we should split into per-locale JSON files.
 */

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

export const STRINGS = {
  en: {
    appName: "Ventura Sentinel",
    appTagline: "AI-Powered Critical Infrastructure Monitoring",
    nav: {
      dashboard: "Dashboard",
      incidents: "Incidents",
      assets: "Assets",
      logout: "Sign out",
      language: "Language",
      bahasa: "Bahasa Melayu",
      english: "English",
    },
    login: {
      title: "Sentinel Console",
      subtitle: "Sign in to monitor critical infrastructure",
      email: "Email",
      emailPlaceholder: "operator@sentinel.local",
      password: "Password",
      passwordPlaceholder: "Your password",
      submit: "Sign in",
      submitting: "Signing in…",
      invalidCredentials: "Invalid email or password.",
      locked: "Account temporarily locked. Try again in 15 minutes.",
      generalError: "Sign-in failed. Please try again.",
    },
    dashboard: {
      title: "Operator Dashboard",
      welcome: "Welcome",
      activeAlerts: "Active alerts",
      draftIncidents: "Awaiting confirmation",
      acknowledged: "Acknowledged",
      resolvedToday: "Resolved today",
      noIncidents: "No incidents to display.",
      loading: "Loading incidents…",
      filterStatus: "Status",
      filterAll: "All",
      refresh: "Refresh",
    },
    incident: {
      title: "Incident",
      status: "Status",
      eventType: "Event type",
      site: "Site",
      segment: "Segment",
      confidence: "Confidence",
      reasoning: "AI reasoning",
      action: "Recommended action",
      sla: "SLA",
      breachedIn: "breached in",
      breached: "BREACHED",
      audit: "Audit trail",
      confirm: "Confirm",
      dismiss: "Dismiss",
      escalate: "Escalate to supervisor",
      resolve: "Resolve",
      back: "Back to dashboard",
      noAccess: "You do not have access to this incident.",
      contextSnapshot: "Context at time of detection",
      activeWorkPermits: "Active work permits",
      trainsInWindow: "Trains in window",
      weather: "Weather",
      baseline: "Baseline RMS",
      none: "none",
      naturalLanguageSummary: "Plain-language summary",
    },
    dismissModal: {
      title: "Dismiss incident",
      reason: "Dismiss reason",
      reasons: {
        VEHICLE_VIBRATION: "Vehicle vibration",
        MAINTENANCE_ACTIVITY: "Maintenance activity",
        ENVIRONMENTAL_NOISE: "Environmental noise",
        DUPLICATE: "Duplicate",
        OTHER: "Other (notes required)",
      },
      notes: "Notes",
      notesRequired: "Notes are required when reason is OTHER (≥10 chars)",
      cancel: "Cancel",
      submit: "Confirm dismiss",
    },
    statusBadge: {
      DRAFT: "Awaiting confirm",
      ACTIVE: "Active",
      ACKNOWLEDGED: "Acknowledged",
      INVESTIGATING: "Investigating",
      RESOLVED: "Resolved",
    },
  },
  ms: {
    appName: "Ventura Sentinel",
    appTagline: "Pemantauan Infrastruktur Kritikal Berkuasa AI",
    nav: {
      dashboard: "Papan Pemuka",
      incidents: "Insiden",
      assets: "Aset",
      logout: "Log keluar",
      language: "Bahasa",
      bahasa: "Bahasa Melayu",
      english: "English",
    },
    login: {
      title: "Konsol Sentinel",
      subtitle: "Log masuk untuk memantau infrastruktur kritikal",
      email: "E-mel",
      emailPlaceholder: "operator@sentinel.local",
      password: "Kata laluan",
      passwordPlaceholder: "Kata laluan anda",
      submit: "Log masuk",
      submitting: "Sedang log masuk…",
      invalidCredentials: "E-mel atau kata laluan tidak sah.",
      locked: "Akaun disekat sementara. Cuba lagi dalam 15 minit.",
      generalError: "Log masuk gagal. Sila cuba lagi.",
    },
    dashboard: {
      title: "Papan Pemuka Operator",
      welcome: "Selamat datang",
      activeAlerts: "Amaran aktif",
      draftIncidents: "Menunggu pengesahan",
      acknowledged: "Diakui",
      resolvedToday: "Diselesaikan hari ini",
      noIncidents: "Tiada insiden untuk dipaparkan.",
      loading: "Memuatkan insiden…",
      filterStatus: "Status",
      filterAll: "Semua",
      refresh: "Muat semula",
    },
    incident: {
      title: "Insiden",
      status: "Status",
      eventType: "Jenis kejadian",
      site: "Tapak",
      segment: "Segmen",
      confidence: "Keyakinan",
      reasoning: "Sebab AI",
      action: "Tindakan disyorkan",
      sla: "SLA",
      breachedIn: "akan dilanggar dalam",
      breached: "DILANGGAR",
      audit: "Jejak audit",
      confirm: "Sahkan",
      dismiss: "Tolak",
      escalate: "Naikkan kepada penyelia",
      resolve: "Selesaikan",
      back: "Kembali ke papan pemuka",
      noAccess: "Anda tidak mempunyai akses kepada insiden ini.",
      contextSnapshot: "Konteks semasa pengesanan",
      activeWorkPermits: "Permit kerja aktif",
      trainsInWindow: "Kereta api dalam tetingkap",
      weather: "Cuaca",
      baseline: "RMS asas",
      none: "tiada",
      naturalLanguageSummary: "Ringkasan bahasa biasa",
    },
    dismissModal: {
      title: "Tolak insiden",
      reason: "Sebab penolakan",
      reasons: {
        VEHICLE_VIBRATION: "Getaran kenderaan",
        MAINTENANCE_ACTIVITY: "Aktiviti penyelenggaraan",
        ENVIRONMENTAL_NOISE: "Bunyi persekitaran",
        DUPLICATE: "Pendua",
        OTHER: "Lain-lain (nota diperlukan)",
      },
      notes: "Nota",
      notesRequired: "Nota diperlukan apabila sebab adalah LAIN-LAIN (≥10 aksara)",
      cancel: "Batal",
      submit: "Sahkan penolakan",
    },
    statusBadge: {
      DRAFT: "Menunggu sahan",
      ACTIVE: "Aktif",
      ACKNOWLEDGED: "Diakui",
      INVESTIGATING: "Disiasat",
      RESOLVED: "Selesai",
    },
  },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: STRINGS.en },
      ms: { translation: STRINGS.ms },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "ms"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "sentinel.lang",
    },
    react: { useSuspense: false },
  });

export default i18n;
